<script setup lang="ts">
  import UnifiedChatEntry from '@/components/app/chat/sidepanel/UnifiedChatEntry.vue'
  import AnnouncementBanner from '@/components/common/AnnouncementBanner.vue'
  import AiAssistantBetaModal from '@/components/agent-actions/ai-assistant-beta/AiAssistantBetaModal.vue'
  import { useAiAssistantBeta } from '@/components/agent-actions/ai-assistant-beta/useAiAssistantBeta'
  import Toaster from '@/components/ui/toast/Toaster.vue'
  import { SIDE_PANEL_WIDTH_PX } from '@/constants/sidePanel'
  import { useUnifiedChatStore } from '@/store/app/unifiedChat'
  import { ConfigProvider } from 'radix-vue'
  import { onBeforeUnmount, onMounted, watch } from 'vue'
  import { RouterView } from 'vue-router'

  const unifiedChat = useUnifiedChatStore()
  const { shouldShowModalOnMount, openModal } = useAiAssistantBeta()

  // shouldShowModalOnMount depends on the PostHog feature-flag check (async) and the user store
  // (also async on cold load). A one-shot read in onMounted would race both. Watch instead so
  // the modal opens whenever the gate first becomes true within this page-load.
  watch(
    shouldShowModalOnMount,
    (should) => {
      if (should) openModal()
    },
    { immediate: true }
  )

  /**
   * Detect classic (space-reserving) scrollbars and opt the document into the
   * ``.has-classic-scrollbars`` CSS class so :file:`assets/index.css` can promote
   * ``<html>`` to ``overflow-y: scroll``. See that rule's docblock for why we want this
   * (anchors the scrollbar to html so radix-vue scroll-lock can no longer remove it on
   * Dialog open) and why it's gated to classic-scrollbar systems only (overlay-scrollbar
   * OSes don't have the layout-shift / disappearing-scrollbar problem in the first
   * place).
   *
   * The probe creates a sacrificial 100x100 div with ``overflow: scroll`` and compares
   * its outer width to its content width. On classic-scrollbar systems the scrollbar
   * eats from the content box so ``offsetWidth > clientWidth``; on overlay-scrollbar
   * systems they're equal. Runs once on app mount, before any Dialog can be opened.
   * Mid-session OS preference flips ("Show scroll bars" toggle) require a page reload
   * — which is the OS's documented behavior anyway, so it's not worth listening for.
   */
  onMounted(() => {
    const probe = document.createElement('div')
    probe.style.cssText =
      'overflow:scroll;width:100px;height:100px;position:absolute;top:-9999px;visibility:hidden;pointer-events:none'
    document.body.appendChild(probe)
    const hasClassicScrollbars = probe.offsetWidth > probe.clientWidth
    document.body.removeChild(probe)

    if (hasClassicScrollbars) {
      document.documentElement.classList.add('has-classic-scrollbars')
    }
  })

  /**
   * JS-side companion to the ``html { overflow-y: scroll }`` strategy in
   * :file:`assets/index.css`.
   *
   * The CSS rule anchors the vertical scrollbar to ``<html>`` and (intentionally) blocks
   * radix-vue's ``body { overflow: hidden }`` from propagating to the viewport — that's
   * what keeps the scrollbar from disappearing and the page from shifting when a Dialog
   * opens. The downside of blocking that propagation is that the browser also stops
   * locking page scroll natively; the user can still mouse-wheel the dimmed background
   * out from under the modal. We restore scroll-lock here in JS instead.
   *
   * Mechanism:
   *
   * 1. ``MutationObserver`` watches body's inline style. radix-vue toggles
   *    ``body.style.overflow`` between ``''`` and ``'hidden'`` whenever any modal popover
   *    that opts into scroll-lock (Dialog, AlertDialog, modal DropdownMenu, ContextMenu,
   *    Menubar, ...) opens or closes.
   * 2. While body is in the locked state, three capture-phase listeners on ``document``
   *    intercept ``wheel``, ``touchmove``, and the keyboard scroll keys (arrows, Page,
   *    Home, End, Space) and ``preventDefault()`` them — but **only** when the event
   *    target sits *outside* any open dialog / menu. Inside those surfaces the browser
   *    keeps its native scroll behavior so internally-scrollable popovers (long prompt
   *    lists, settings dialogs, ...) work normally. The ``overscroll-behavior: contain``
   *    rule on dialog roles in :file:`assets/index.css` then absorbs scroll-chain at the
   *    inner boundary so reaching the bottom of a scrollable dialog doesn't kick the
   *    page underneath.
   * 3. The ``capture: true`` flag is important: we need to win the race against any
   *    bubble-phase listener that might call ``stopPropagation()`` after handling its
   *    own wheel event but before our ``preventDefault()`` runs.
   *
   * This is a no-op on overlay-scrollbar systems where radix's body overflow:hidden
   * still propagates to the viewport (no html overflow rule applied) and the browser
   * locks scroll for free — the listeners attach but every event delivered while body is
   * locked has nothing to scroll anyway. Cheap enough not to bother gating on the
   * ``has-classic-scrollbars`` class.
   */
  let scrollLockObserver: MutationObserver | null = null

  const SCROLL_KEYS = new Set([
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'PageUp',
    'PageDown',
    'Home',
    'End',
    ' '
  ])

  /**
   * Scroll surfaces that the modal scroll-lock guard MUST keep usable while body's
   * ``overflow: hidden`` is in effect.
   *
   * - ``dialog`` / ``alertdialog`` / ``menu`` / ``listbox``: the modal popovers radix-vue's
   *   scroll-lock is meant to protect — internally-scrollable popover bodies (long prompt
   *   lists, settings dialogs, etc.) must keep working.
   * - ``complementary``: the AI side panel (``SidePanel.vue`` shell sets this role on its
   *   ``<aside>``). It has its own ``overflow-y: auto`` scroll container for the message
   *   list and is conceptually a sibling surface to the rest of the app, not part of the
   *   page that scroll-lock is meant to freeze. Without this entry, opening any Dialog
   *   while the side panel is open would also strand the chat thread — the user couldn't
   *   wheel through messages until the dialog closes. The page's html-level scroll is
   *   still locked because ``<html>`` does not carry ``role="complementary"``.
   */
  const SCROLL_ALLOWED_INSIDE =
    '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [role="complementary"]'

  const isInsideScrollSurface = (target: EventTarget | null): boolean => {
    return target instanceof Element && target.closest(SCROLL_ALLOWED_INSIDE) !== null
  }

  const onWheelOrTouch = (event: Event) => {
    if (!isInsideScrollSurface(event.target)) {
      event.preventDefault()
    }
  }

  const onScrollKey = (event: KeyboardEvent) => {
    if (!SCROLL_KEYS.has(event.key)) return
    const target = event.target
    if (!(target instanceof Element)) return
    // Don't interfere with text input / textarea / contenteditable — Space and arrows are
    // text-editing actions there, not scroll requests.
    if (target.matches('input, textarea, select, [contenteditable="true"]')) return
    if (isInsideScrollSurface(target)) return
    event.preventDefault()
  }

  let scrollPreventionAttached = false

  const attachScrollPrevention = () => {
    if (scrollPreventionAttached) return
    document.addEventListener('wheel', onWheelOrTouch, { passive: false, capture: true })
    document.addEventListener('touchmove', onWheelOrTouch, { passive: false, capture: true })
    document.addEventListener('keydown', onScrollKey as EventListener, { capture: true })
    scrollPreventionAttached = true
  }

  const detachScrollPrevention = () => {
    if (!scrollPreventionAttached) return
    document.removeEventListener('wheel', onWheelOrTouch, { capture: true } as EventListenerOptions)
    document.removeEventListener('touchmove', onWheelOrTouch, {
      capture: true
    } as EventListenerOptions)
    document.removeEventListener(
      'keydown',
      onScrollKey as EventListener,
      {
        capture: true
      } as EventListenerOptions
    )
    scrollPreventionAttached = false
  }

  onMounted(() => {
    scrollLockObserver = new MutationObserver(() => {
      if (document.body.style.overflow === 'hidden') {
        attachScrollPrevention()
      } else {
        detachScrollPrevention()
      }
    })
    scrollLockObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    })
  })

  onBeforeUnmount(() => {
    scrollLockObserver?.disconnect()
    scrollLockObserver = null
    detachScrollPrevention()
  })
</script>

<template>
  <!--
    Three-part defense against radix-vue's modal scroll-lock side effects on classic-
    scrollbar OSes (macOS "Show scroll bars: Always", Windows, most Linux desktops):

    1. ``html.has-classic-scrollbars { overflow-y: scroll }`` in :file:`assets/index.css`
       (gated on the runtime probe in this file's ``onMounted``) anchors the vertical
       scrollbar to ``<html>``. Since html now has explicit non-visible overflow, body's
       ``overflow: hidden`` no longer propagates to the viewport per CSS spec, so the
       scrollbar can't be removed when a Dialog opens — viewport width stays constant,
       page content doesn't reflow, and the side-panel chat / floating button don't drift.

    2. ``ConfigProvider :scroll-body="false"`` here suppresses radix's body padding-right
       compensation. The compensation only exists to cover the gap left when the scrollbar
       is removed; with (1) keeping the scrollbar in place, there's no gap to cover, and
       writing ``padding-right: 15px`` would visibly shift body content sideways. Setting
       ``scroll-body=false`` makes radix write ``padding-right: 0`` instead, which is the
       no-op we want. The overflow:hidden, focus trap, dimming overlay, and ``--scrollbar-
       width`` export still happen.

    3. The ``MutationObserver`` in this file's ``onMounted`` restores actual scroll-lock
       in JS. Blocking the body→viewport overflow propagation in (1) also removes the
       browser's native scroll-lock for free, so the user could otherwise wheel the dimmed
       background out from under a modal. The observer attaches capture-phase wheel /
       touchmove / scroll-key handlers whenever body's overflow flips to ``hidden`` and
       absorbs every event whose target isn't inside an open dialog / menu / listbox.
  -->
  <ConfigProvider :scroll-body="false">
    <!--
      When the AI side panel is open we shrink the app content area by its width so the panel
      behaves as a sidebar (pushing layout) rather than a floating overlay.
    -->
    <!--
      ``motion-safe:`` gates Tailwind's ``transition-[padding]`` on
      ``@media (prefers-reduced-motion: no-preference)``, so the 300ms reflow of the page
      content area when the side panel opens / closes runs only for users who haven't asked
      the OS to reduce motion. Reduced-motion users still see the layout settle into the new
      width — they just see it instantly instead of as a slide, which is exactly the contract
      ``prefers-reduced-motion: reduce`` is meant to deliver.
    -->
    <div
      class="duration-300 ease-out motion-safe:transition-[padding]"
      :style="{ paddingRight: unifiedChat.isSidePanelOpen ? `${SIDE_PANEL_WIDTH_PX}px` : '0px' }"
    >
      <AnnouncementBanner />
      <RouterView :key="$route.path" />
    </div>
    <UnifiedChatEntry />
    <AiAssistantBetaModal />
    <Toaster />
  </ConfigProvider>
</template>
